import React, { useEffect, useState } from "react";
import ChartAndTable from "./ChartAndTable";
import {
  prepareChartDataForPageActivityOne,
  prepareChartDataForPageActivityTwo,
  processPageActivityData,
  processPageActivityTableData,
} from "./prepareChartData";
import { gql, useQuery } from "@apollo/client";
import Select from "react-select";
import { Loading, LoadTable } from "../../Components";
import RefreshIcon from "./RefreshIcon";
import "./Dashboard.scss";
const PageActivityQuery = gql`
  query Query {
    GetPageActivityForChart
  }
`;

const ActivityTableQuery = gql`
  query Query($timeZone: String) {
    GetPageActivityForTableByTimeZone(timeZone: $timeZone)
  }
`;

const customStyles = {
  container: (provided) => ({
    ...provided,
    width: "200px",
    marginBottom: "10px",
    marginLeft: "1%",
  }),
  control: (provided) => ({
    ...provided,
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    boxShadow: "none",
    fontSize: "12px",
    padding: "0px",
    "&:hover": {
      borderColor: "#888",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#888",
    fontSize: "12px",
    padding: "0px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#999999",
    fontSize: "11px",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: "11px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "150px",
    overflowY: "auto",
    fontSize: "11px",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#b3e5b4" : state.isFocused ? "#4caf50" : base.backgroundColor,
    color: state.isSelected || state.isFocused ? "#fff" : "#333",
    fontSize: "11px",
    "&:active": {
      backgroundColor: "#b3e5b4",
      color: "#fff",
    },
    "&:hover": {
      backgroundColor: "#4caf50",
      color: "#fff",
    },
  }),
};

const PageActivity = () => {
  const Key = "Page_Activity";
  const tableKey = "Activity_Table";
  const [theme, setThemeOption] = useState("Theme1");
  const [chartData, setChartData] = useState({});
  const [maxNumber, setMaxNumber] = useState(10000);
  const [options, setOption] = useState({});
  const [TableData, setTableData] = useState([]);
  const [ActivityTableData, setActivityTableData] = useState([]);
  const [colAddition, setcolAddittion] = useState([]);
  const [ActivityAdd, setActivityAdd] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState({ value: "today", label: "Today" });

  //chart
  const { data, loading, error, refetch, networkStatus } = useQuery(PageActivityQuery, {
    context: { headers: { "X-GraphQL-Server": "secondary" } },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (data?.GetPageActivityForChart) {
      const { ColAddition, updatedData } = processPageActivityData(data?.GetPageActivityForChart);
      setTableData(updatedData);
      setcolAddittion(ColAddition);
    }
  }, [data]);

  //Activity table
  const selectOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "sevenDaysAgo", label: "Past 7 Days" },
    { value: "currentMonth", label: "Current Month" },
    { value: "lastMonth", label: "Last Month" },
  ];

  const handleSelectChange = (Opt) => {
    setSelectedPeriod(Opt);
  };

  const {
    data: activityTable,
    loading: activityLoading,
    error: activityError,
    refetch: activityRefetch,
    networkStatus: activityNetworkRefetch,
  } = useQuery(ActivityTableQuery, {
    variables: { timeZone: selectedPeriod.value },
    context: { headers: { "X-GraphQL-Server": "secondary" } },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (activityTable?.GetPageActivityForTableByTimeZone) {
      const { ColAddition, updatedData } = processPageActivityTableData(
        activityTable?.GetPageActivityForTableByTimeZone
      );
      console.log(ColAddition, updatedData)
      setActivityTableData(updatedData);
      setActivityAdd(ColAddition);
    }
  }, [activityTable, selectedPeriod]);

  useEffect(() => {
    if (data?.GetPageActivityForChart) {
      let preparedData;
      if (theme === "Theme1") {
        preparedData = prepareChartDataForPageActivityOne(data?.GetPageActivityForChart);
      } else {
        preparedData = prepareChartDataForPageActivityTwo(
          data?.GetPageActivityForChart,
          "#fcb900",
          "Total Activity Count"
        );
      }
      setChartData(preparedData);
      const max = preparedData.datasets?.reduce((max, dataset) => Math.max(max, ...dataset.data), 0) || 0;
      setMaxNumber(max + max * 0.1);
    }
  }, [data, theme]);

  useEffect(() => {
    const options1 = {
      responsive: true,
      plugins: {
        legend: { position: "top" },
        title: { display: false, text: "Page Activity" },
      },
      scales: {
        x: {
          reverse: true,
          title: { display: true, text: "Date" },
          ticks: { maxRotation: 90, minRotation: 90, autoSkip: false },
        },
        y: { min: 0, max: maxNumber, ticks: { stepSize: 10 }, title: { display: true, text: "Activity Count" } },
      },
    };
    const options2 = {
      responsive: true,
      plugins: {
        legend: { position: "top" },
        title: { display: false, text: "Page Activity" },
        tooltip: {
          callbacks: {
            label: function (context) {
              return context.dataset.label + " : " + context.parsed.y;
            },
            afterBody: function (tooltipItems) {
              const tooltipIndex = tooltipItems[0]?.dataIndex;
              const details = chartData?.tooltips[tooltipIndex];
              return details ? details : "";
            },
          },
        },
      },
      scales: {
        x: {
          reverse: true,
          title: { display: true, text: "Date" },
          ticks: { maxRotation: 90, minRotation: 90, autoSkip: false },
        },
        y: {
          min: 0,
          max: maxNumber,
          ticks: { stepSize: 10 },
          title: { display: true, text: "Activity Count" },
        },
      },
    };
    setOption(theme === "Theme1" ? options1 : options2);
  }, [theme, maxNumber]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <p></p>;
  }
  console.log(ActivityTableData)
  return (
    <>
      <div className="title_With_Loading_Logo">
        <div className="load-post-title">Load Post By Erp User</div>
        <div className="load-post-logo">
          <RefreshIcon handleRefetch={refetch} loading={loading} />
        </div>
      </div>
      <div style={{ display: "flex" }}>
        <div style={{ marginLeft: "1%" }}>
          <input
            type="radio"
            name="themeOptionForPageActivity"
            value="Theme1"
            checked={theme === "Theme1"}
            onChange={() => setThemeOption("Theme1")}
          />
          Theme1
          <input
            type="radio"
            name="themeOptionForPageActivity"
            value="Theme2"
            checked={theme === "Theme2"}
            onChange={() => setThemeOption("Theme2")}
          />
          Theme2
        </div>
        <div style={{ marginLeft: "5%" }}>
          {/* <RefreshButton
            isRefetching={isRefetching}
            onRefresh={handleRefresh}
            time={{ hours: Math.floor(time / 3600), minutes: Math.floor((time % 3600) / 60), seconds: time % 60 }}
          /> */}
        </div>
      </div>
      <ChartAndTable
        chartData={chartData}
        options={options}
        data={TableData}
        error={error}
        tableKey={Key}
        colAddition={colAddition}
      />
      <Select
        value={selectOptions.value}
        onChange={handleSelectChange}
        options={selectOptions}
        placeholder={selectedPeriod.label}
        styles={customStyles}
      />
      <h3 class="load-Post-title">Page Activity Of Accounts</h3>
      <div className="Page_Activity_Of_Accounts_Box">
        <LoadTable
          data={ActivityTableData}
          // loading={activityLoading}
          error={activityError}
          title={tableKey}
          settings={false}
          options={false}
          TableHeaders={ActivityAdd}
        />
      </div>
    </>
  );
};

export default PageActivity;
